import { ControlDescriptor, ControlGroup, ControlOption } from '~/routes/keyboard-help/controls';

type Select = Extract<ControlDescriptor, { type: 'select' }>;

// The television's own "+ New singer…" opens its profile form; the phone names singers itself.
export const TV_NEW_SINGER = 'new-singer';

export interface Slot {
  player: number;
  group: ControlGroup;
  label: string;
  input?: Select;
  singer?: Select;
  newSinger?: ControlDescriptor;
  add?: ControlDescriptor;
  remove?: ControlDescriptor;
  filled: boolean;
  // An empty slot that cannot be filled until the song ends - the roster is fixed while one plays.
  waitsForSong: boolean;
  device?: ControlOption;
  singerName?: string;
}

// A phone arrives in the input list as "📱Ana"; the phone draws its own glyph instead.
export const deviceLabel = (option: ControlOption) => option.label.replace(/^📱\s*/u, '');

const byName = (controls: ControlDescriptor[], name: string) => controls.find((control) => control.name === name);

const asSelect = (control: ControlDescriptor | undefined) => (control?.type === 'select' ? control : undefined);

// One slot per group the television sent, in the order it registered them.
export function buildSlots(controls: ControlDescriptor[]): Slot[] {
  const groups: ControlGroup[] = [];
  for (const control of controls) {
    const group = control.group;
    if (group?.player !== undefined && !groups.some((known) => known.id === group.id)) groups.push(group);
  }

  return groups.map((group) => {
    const player = group.player!;
    const own = controls.filter((control) => control.group?.id === group.id);
    const input = asSelect(byName(own, `player-${player}-input`));
    const singer = asSelect(byName(own, `player-${player}-singer`));
    const add = byName(own, `player-${player}-add`);
    const remove = byName(own, `player-${player}-remove`);
    const filled = !add && (!!remove || !!input?.value);

    return {
      player,
      group,
      label: `Player ${player + 1}`,
      input,
      singer,
      newSinger: byName(own, `player-${player}-new-singer`),
      add,
      remove,
      filled,
      waitsForSong: !filled && !!add?.disabled,
      device: filled ? input?.options.find((option) => option.value === input.value) : undefined,
      singerName: singer?.options.find((option) => option.value === singer.value)?.label,
    };
  });
}

export type Press = { kind: 'send'; value: string } | { kind: 'toast'; message: string } | { kind: 'none' };

export interface MicOptionView {
  label: string;
  note: string;
  selected: boolean;
  taken: boolean;
  thisPhone: boolean;
  press: Press;
}

export function micOptionView(slot: Slot, option: ControlOption, myMicId: string | null): MicOptionView {
  const thisPhone = !!myMicId && option.micId === myMicId;
  const selected = slot.filled && option.value === slot.input?.value;
  const taken = !!option.disabled;
  const label = thisPhone ? 'This phone' : deviceLabel(option);

  const press: Press =
    taken && thisPhone
      ? { kind: 'toast', message: `This phone is already ${option.note}. Leave that slot first.` }
      : taken
        ? { kind: 'toast', message: `${option.note} is using ${label}` }
        : selected
          ? { kind: 'none' }
          : { kind: 'send', value: option.value };

  return {
    label,
    note: taken ? (thisPhone ? `You're ${option.note}` : (option.note ?? '')) : selected ? 'Selected' : '',
    selected,
    taken,
    thisPhone,
    press,
  };
}

export function singerPress(slot: Slot, option: ControlOption): Press {
  if (option.disabled) return { kind: 'toast', message: `${option.label} is ${option.note}` };
  // A phone names its own singer, and that name beats any assignment made here.
  if (slot.singer?.disabled) return { kind: 'toast', message: `The phone on ${slot.label} names its own singer` };
  if (option.value === slot.singer?.value) return { kind: 'none' };
  return { kind: 'send', value: option.value };
}
